import type { UserProfile } from "../types/domain";
import { StationRole, SystemRole } from "../types/common";

const isSuperAdminProfile = (userProfile: UserProfile | null): boolean =>
  userProfile?.role === SystemRole.SUPERADMIN;

/** Rol ACTIVO del usuario en la estación, sin considerar el rol global. */
const activeStationRole = (
  userProfile: UserProfile | null,
  stationId: string | null
): StationRole | null =>
  (userProfile?.station_roles?.find(
    (role) => role.station_id === stationId && role.is_active
  )?.station_role as StationRole | undefined) ?? null;

const hasActiveRoleAnywhere = (
  userProfile: UserProfile | null,
  stationRole: StationRole
): boolean =>
  userProfile?.station_roles?.some(
    (role) => role.station_role === stationRole && role.is_active
  ) ?? false;

/** Verifica si un usuario tiene acceso a una estación. */
export const hasStationAccess = (
  userProfile: UserProfile | null,
  stationId: string
): boolean => isSuperAdminProfile(userProfile) || activeStationRole(userProfile, stationId) !== null;

/** Rol del usuario en una estación (el superadmin cuenta como admin). */
export const getStationRole = (
  userProfile: UserProfile | null,
  stationId: string
): StationRole | null =>
  isSuperAdminProfile(userProfile) ? StationRole.ADMIN : activeStationRole(userProfile, stationId);

/**
 * Verifica si un usuario puede editar la ESTACIÓN en sí (fila de `stations`).
 * La policy UPDATE de `stations` es EXCLUSIVA del superadmin, así que la UI solo
 * ofrece el lápiz de editar estación al superadmin.
 */
export const canEditStation = (userProfile: UserProfile | null): boolean =>
  isSuperAdminProfile(userProfile);

/**
 * Verifica si un usuario puede gestionar el CONTENIDO de una estación (grupos,
 * sensores): la RLS de `groups` y `sensors` permite UPDATE a admin y operator
 * de la estación, además del superadmin. `stationId` puede ser null: un sensor
 * FÍSICO en inventario aún no tiene estación y solo el superadmin lo edita.
 */
export const canManageStationContent = (
  userProfile: UserProfile | null,
  stationId: string | null
): boolean => {
  if (isSuperAdminProfile(userProfile)) return true;
  const role = activeStationRole(userProfile, stationId);
  return role === StationRole.ADMIN || role === StationRole.OPERATOR;
};

/**
 * IDs de las estaciones donde el usuario es administrador ACTIVO. El superadmin
 * no entra aquí (su alcance es global y no se deriva de membresías).
 */
const adminStationIds = (userProfile: UserProfile | null): Set<string> =>
  new Set(
    (userProfile?.station_roles ?? [])
      .filter((r) => r.station_role === StationRole.ADMIN && r.is_active)
      .map((r) => r.station_id)
  );

/** Única fuente de verdad de la autorización en la UI (la RLS manda). */
export const usePermissions = (userProfile: UserProfile | null) => {
  const isSuperAdmin = isSuperAdminProfile(userProfile);
  const isAtLeastStationAdmin = isSuperAdmin || hasActiveRoleAnywhere(userProfile, StationRole.ADMIN);
  // Operador ACTIVO en alguna estación: la RLS de alertas le permite crear o
  // editar reglas y reconocer o resolver alertas de SUS estaciones.
  const isStationOperator = hasActiveRoleAnywhere(userProfile, StationRole.OPERATOR);

  return {
    isSuperAdmin: () => isSuperAdmin,

    /** Gestionar estaciones, grupos, sensores y dispositivos. */
    canManageRegistration: () => isAtLeastStationAdmin,

    /** Gestionar actuadores y sus reglas. */
    canManageActuators: () => isAtLeastStationAdmin,

    /**
     * Gestionar reglas de alerta y reconocer/resolver alertas: superadmin, admin
     * u OPERADOR de estación (la RLS lo acota a los sensores de su estación).
     */
    canManageAlertRules: () => isAtLeastStationAdmin || isStationOperator,

    /** Configuración global del sistema. */
    canManageSystemSettings: () => isSuperAdmin,

    /**
     * Gestionar los MIEMBROS de una estación. Sin `stationId` responde "¿puede
     * en ALGUNA estación?" (menú/entrada a la página); con `stationId`, en esa.
     */
    canManageStationMembers: (stationId?: string) => {
      if (isSuperAdmin) return true;
      if (stationId === undefined) return isAtLeastStationAdmin;
      return adminStationIds(userProfile).has(stationId);
    },

    /** Estaciones donde el usuario es admin activo (vacío para superadmin: su
     * alcance es global, las páginas usan la lista completa de estaciones). */
    adminStationIds: () => adminStationIds(userProfile),
  };
};
